import React, { useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';

import {
  PaginationButtonPrev,
  PaginationButtonNext,
  PaginationContainer,
  Icon,
  CountTotal,
} from './styles';

import { PaginationProps } from './types';
import { PageSizeSelector } from '../PageSizeSelector';

import NextPageIcon from '../../../../../assets/icons/next-page-icon.svg';
import PrevPageIcon from '../../../../../assets/icons/prev-page-icon.svg';

export const PaginationButtons = ({
  currentPage,
  pageCount,
  totalCount,
  pageIndex,
  previousPage,
  nextPage,
  className,
  isDisabled,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
}: PaginationProps): JSX.Element => {
  const { t } = useTranslation(['tables', 'common']);
  // Tarea #11: el selector se renderiza si la tabla pasó pageSize +
  // onPageSizeChange. Sin diferenciación por viewport (tablet y desktop
  // se comportan igual). Permite migración tabla a tabla sin tocar a
  // las que aún no han sido convertidas.
  const showPageSizeSelector =
    pageSize !== undefined && onPageSizeChange !== undefined;

  const prevRef = useRef(previousPage);
  const nextRef = useRef(nextPage);
  const disabledRef = useRef(isDisabled);
  const pageCountRef = useRef(pageCount);
  const currentPageRef = useRef(currentPage);
  prevRef.current = previousPage;
  nextRef.current = nextPage;
  disabledRef.current = isDisabled;
  pageCountRef.current = pageCount;
  currentPageRef.current = currentPage;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return; // Ignorar auto-repeat al mantener tecla pulsada
      if (disabledRef.current || pageCountRef.current <= 1) return;
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (active?.isContentEditable) return;
      if (active?.closest('[role="dialog"]')) return;

      if (e.key === 'ArrowLeft') {
        // Paridad con el botón mouse: no permitir retroceder en p1.
        if (currentPageRef.current <= 1) return;
        e.preventDefault();
        prevRef.current();
      } else if (e.key === 'ArrowRight') {
        // Paridad con el botón mouse: no permitir avanzar más allá de la
        // última página. El botón ya está disabled por `currentPage === pageCount`
        // pero el handler de teclado no chequeaba este boundary y permitía
        // "12 de 11" pasando del límite.
        if (currentPageRef.current >= pageCountRef.current) return;
        e.preventDefault();
        nextRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <PaginationContainer className={className}>
      {showPageSizeSelector && pageSize && onPageSizeChange && (
        <PageSizeSelector
          value={pageSize}
          onChange={onPageSizeChange}
          disabled={isDisabled}
          options={pageSizeOptions}
        />
      )}
      {pageCount > 1 && (
        <>
          <PaginationButtonPrev
            onClick={debounce(previousPage, 500, {
              leading: false,
              trailing: true,
            })}
            disabled={isDisabled}
            data-tooltip={`${t('tables:pagination.previous')} (\u2190)`}
          >
            <Icon src={PrevPageIcon} />
          </PaginationButtonPrev>

          <CountTotal size={'XS'}>
            {t('tables:pagination.info', {
              currentPage,
              pageCount,
              totalCount,
            })}
          </CountTotal>
          <PaginationButtonNext
            onClick={debounce(nextPage, 500, {
              leading: false,
              trailing: true,
            })}
            disabled={currentPage === pageCount || isDisabled}
            data-tooltip={`${t('tables:pagination.next')} (\u2192)`}
          >
            <Icon src={NextPageIcon} />
          </PaginationButtonNext>
        </>
      )}
    </PaginationContainer>
  );
};
